// Reading a spreadsheet of members. Pure functions only (no network), so every rule is unit-tested.
//
// Input is plain text: the contents of a .csv file (Excel: File > Save As > CSV) or cells copied from
// Excel / Google Sheets and pasted, which arrive tab-separated. Nothing here touches the database;
// src/data/memberImport.ts does that, one person at a time, through the same function the Add member
// form uses, so phone matching (no duplicate people across departments) is the same.
import { normalizeZambianPhone } from './phone'

export const MAX_IMPORT_ROWS = 500

/** Split text into rows of cells. Handles quotes, a leading BOM, and comma, semicolon or tab separators. */
export function parseDelimited(input: string): string[][] {
  const text = input.replace(/^﻿/, '')
  const firstLine = text.split(/\r\n|\n|\r/).find((l) => l.trim() !== '') ?? ''
  const count = (ch: string) => firstLine.split(ch).length - 1
  const delim = count('\t') > 0 ? '\t' : count(';') > count(',') ? ';' : ','

  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  const endCell = () => { row.push(cell); cell = '' }
  const endRow = () => {
    endCell()
    if (row.some((c) => c.trim() !== '')) rows.push(row.map((c) => c.trim()))
    row = []
  }
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++ } else quoted = false
      } else cell += ch
    } else if (ch === '"' && cell === '') quoted = true
    else if (ch === delim) endCell()
    else if (ch === '\r') { if (text[i + 1] === '\n') i++; endRow() }
    else if (ch === '\n') endRow()
    else cell += ch
  }
  if (cell !== '' || row.length > 0) endRow()
  return rows
}

type Field = 'name' | 'first' | 'last' | 'phone' | 'email' | 'joined' | 'notes'
const ALIASES: Record<Field, string[]> = {
  name: ['name', 'fullname', 'membername', 'member', 'names'],
  first: ['firstname', 'first', 'givenname', 'forename'],
  last: ['lastname', 'last', 'surname', 'familyname'],
  phone: ['phone', 'phonenumber', 'mobile', 'mobilenumber', 'cell', 'cellphone', 'contact', 'tel', 'telephone', 'whatsapp', 'number'],
  email: ['email', 'emailaddress', 'mail'],
  joined: ['joined', 'joinedon', 'datejoined', 'joindate', 'dateofjoining', 'dateofjoin', 'since'],
  notes: ['notes', 'note', 'comment', 'comments', 'remarks'],
}
const squash = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '')

/** Which column holds what. Without a recognisable header row, columns are taken as name, phone, email. */
export function detectColumns(rows: string[][]): { map: Partial<Record<Field, number>>; hasHeader: boolean } {
  const head = rows[0] ?? []
  const map: Partial<Record<Field, number>> = {}
  head.forEach((h, i) => {
    const key = squash(h)
    for (const f of Object.keys(ALIASES) as Field[]) {
      if (map[f] === undefined && ALIASES[f].includes(key)) map[f] = i
    }
  })
  // Any recognised heading makes the first row a header (a missing Name column is then reported, not
  // silently read as data).
  const hasHeader = Object.keys(map).length > 0
  if (hasHeader) return { map, hasHeader }
  return { map: { name: 0, phone: 1, email: 2 }, hasHeader: false }
}

/** 'YYYY-MM-DD', or day-first 'D/M/YYYY', 'D-M-YYYY', 'D.M.YYYY'. Returns null if it is not a real date. */
export function parseJoinDate(raw: string): string | null {
  const s = raw.trim()
  let y: number, m: number, d: number
  let hit = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s)
  if (hit) { y = +hit[1]; m = +hit[2]; d = +hit[3] } else {
    hit = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s)
    if (!hit) return null
    d = +hit[1]; m = +hit[2]; y = +hit[3]
  }
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate() // days in that month
  if (y < 1900 || m < 1 || m > 12 || d < 1 || d > last) return null
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export interface ImportRow {
  /** The row's line number in the file as the person sees it (the header is line 1). */
  line: number
  fullName: string
  /** Canonical +260XXXXXXXXX, or null when none was given. */
  phone: string | null
  email: string | null
  notes: string | null
  joinedOn: string
}

export type RowVerdict =
  | { kind: 'ready'; row: ImportRow }
  | { kind: 'problem'; line: number; name: string; reasons: string[] }
  /** Already in this department, or repeated earlier in the same file: skipped, not an error. */
  | { kind: 'skip'; line: number; name: string; reason: string }

export interface ImportPlan {
  verdicts: RowVerdict[]
  ready: number
  problems: number
  skipped: number
  hasHeader: boolean
  /** Set when the file as a whole cannot be used (empty, no name column, too many rows). */
  fatal: string | null
}

export interface ExistingMember { fullName: string; phone: string | null; leftOn: string | null }

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Check every row and decide what would happen to it. `today` is the Lusaka date 'YYYY-MM-DD' (used
 * for rows with no join date, and to refuse dates in the future). `existing` is who is already in the
 * department, so a second import of the same file does not add anybody twice.
 */
export function planImport(text: string, existing: ExistingMember[], today: string): ImportPlan {
  const empty = (fatal: string): ImportPlan => ({ verdicts: [], ready: 0, problems: 0, skipped: 0, hasHeader: false, fatal })
  const rows = parseDelimited(text)
  if (rows.length === 0) return empty('There is nothing to import. Paste the rows or choose a file.')
  const { map, hasHeader } = detectColumns(rows)
  if (hasHeader && map.name === undefined && (map.first === undefined || map.last === undefined)) {
    return empty('Could not find a Name column. Put "Name" in the first row of that column.')
  }
  const body = hasHeader ? rows.slice(1) : rows
  if (body.length === 0) return empty('The file has a header row but no people under it.')
  if (body.length > MAX_IMPORT_ROWS) return empty(`That is ${body.length} rows. Import at most ${MAX_IMPORT_ROWS} at a time; split the file and import it in parts.`)

  const get = (r: string[], f: Field) => (map[f] === undefined ? '' : (r[map[f]!] ?? '').trim())
  const currentPhones = new Set(existing.filter((m) => m.leftOn === null && m.phone).map((m) => m.phone))
  const currentNames = new Set(existing.filter((m) => m.leftOn === null).map((m) => norm(m.fullName)))
  const seenPhone = new Map<string, number>()
  const seenName = new Map<string, number>()
  const verdicts: RowVerdict[] = []

  body.forEach((r, i) => {
    const line = i + 1 + (hasHeader ? 1 : 0)
    const name = (get(r, 'name') || `${get(r, 'first')} ${get(r, 'last')}`).replace(/\s+/g, ' ').trim()
    const reasons: string[] = []
    if (name.length < 2) reasons.push('Name is missing (at least 2 letters)')
    const phoneRaw = get(r, 'phone')
    const phone = phoneRaw === '' ? null : normalizeZambianPhone(phoneRaw)
    if (phoneRaw !== '' && phone === null) reasons.push(`Phone "${phoneRaw}" is not a Zambian number like 097 123 4567`)
    const emailRaw = get(r, 'email')
    if (emailRaw !== '' && !EMAIL.test(emailRaw)) reasons.push(`Email "${emailRaw}" is not valid`)
    const joinedRaw = get(r, 'joined')
    let joinedOn = today
    if (joinedRaw !== '') {
      const d = parseJoinDate(joinedRaw)
      if (!d) reasons.push(`Date "${joinedRaw}" is not a date like 25/03/2024`)
      else if (d > today) reasons.push('The date joined is in the future')
      else joinedOn = d
    }
    if (reasons.length > 0) return void verdicts.push({ kind: 'problem', line, name: name || '(no name)', reasons })

    if (phone) {
      if (currentPhones.has(phone)) return void verdicts.push({ kind: 'skip', line, name, reason: 'Already in this department (same phone number)' })
      const first = seenPhone.get(phone)
      if (first !== undefined) return void verdicts.push({ kind: 'skip', line, name, reason: `Same phone number as line ${first}` })
      seenPhone.set(phone, line)
    } else {
      // No number to match on, so the only safe check is the name: a second import must not add them again.
      const key = norm(name)
      if (currentNames.has(key)) return void verdicts.push({ kind: 'skip', line, name, reason: 'Someone with this name is already in this department (no phone number to tell them apart)' })
      const first = seenName.get(key)
      if (first !== undefined) return void verdicts.push({ kind: 'skip', line, name, reason: `Same name as line ${first}, and neither has a phone number` })
      seenName.set(key, line)
    }
    verdicts.push({ kind: 'ready', row: { line, fullName: name, phone, email: emailRaw || null, notes: get(r, 'notes') || null, joinedOn } })
  })

  return {
    verdicts,
    ready: verdicts.filter((v) => v.kind === 'ready').length,
    problems: verdicts.filter((v) => v.kind === 'problem').length,
    skipped: verdicts.filter((v) => v.kind === 'skip').length,
    hasHeader,
    fatal: null,
  }
}

/** The file people can fill in. */
export const IMPORT_TEMPLATE: string[][] = [
  ['Name', 'Phone', 'Email', 'Date joined', 'Notes'],
  ['Mary Banda', '097 123 4567', 'mary@example.com', '25/03/2024', ''],
  ['John Phiri', '0966 000 111', '', '', 'Choir leader'],
]
